import { useEffect, useState } from 'react'

import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  TreeRolesAndLevelsEntitySelectorDocument,
  TreeRolesAndLevelsEntitySelectorQuery,
} from 'generated/resources/graphql'

import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  IJobsAndRatesFormValues,
  SelectedLevelItem,
} from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'

interface ParsedEntity {
  expanded: boolean
  id: string
  name: string
  totalCount?: number
  subItems?: Array<{
    subId: number | string
    subName: string
    subSearchKeys?: string[]
    totalCount: number
  }>
}

type GroupType = (typeof Groups)[keyof typeof Groups]
const Groups = {
  ROLES: 'roles',
  LEVELS: 'levels',
} as const

// Tree implementation parsing functions
const parseTreeDataByJobs = (
  treeLevels: TreeRolesAndLevelsEntitySelectorQuery['jobCatalog']['treeLevelsConnection']['nodes'],
  expandedElements: string[]
): ParsedEntity[] => {
  if (treeLevels.length === 0) {
    return []
  }

  // Group levels by roleName
  type GroupedByRole = Record<
    string,
    Array<{
      subId: number | string
      subName: string
      subSearchKeys: string[]
      totalCount: number
    }>
  >

  const groupedByRole = treeLevels.reduce<GroupedByRole>((acc, level) => {
    const roleName = level.ancestor?.name || 'Unknown Role'

    if (!acc[roleName]) acc[roleName] = []

    acc[roleName].push({
      subId: level.id,
      subName: level.name || '',
      subSearchKeys: [level.name || '', roleName],
      totalCount: level.referenceContractsConnection?.totalCount || 0,
    })

    return acc
  }, {})

  // Convert to EntitySelect format
  return Object.entries(groupedByRole).map(([roleName, levels], index) => {
    const id = `${roleName.toLowerCase().replace(/\s+/g, '_')}_${index}`
    return {
      expanded: expandedElements.includes(id),
      id,
      name: roleName,
      subItems: levels,
    }
  })
}

const parseTreeDataByLevels = (
  treeLevels: TreeRolesAndLevelsEntitySelectorQuery['jobCatalog']['treeLevelsConnection']['nodes']
): ParsedEntity[] => {
  if (treeLevels.length === 0) return []

  return treeLevels.map((level) => ({
    expanded: false,
    id: String(level.id),
    name: level.name || '',
    totalCount: level.referenceContractsConnection?.totalCount || 0,
    subItems: undefined,
  }))
}

const RolesAndLevelsEntitySelector = ({
  selectedLevels,
  setValues,
  existingResourceJobLevelIds,
}: {
  selectedLevels: SelectedLevelItem[]
  setValues: (values: Partial<IJobsAndRatesFormValues>) => void
  existingResourceJobLevelIds: string[]
}) => {
  const [selectedGroup, setSelectedGroup] = useState<GroupType>(Groups.ROLES)
  const [expandedElements, setExpandedElements] = useState<string[]>([])
  const [selectedItems, setSelectedItems] = useState<SelectedLevelItem[]>(selectedLevels)

  const { data: treeData, loading } = useQuery(TreeRolesAndLevelsEntitySelectorDocument)

  const treeLevels = treeData?.jobCatalog?.treeLevelsConnection?.nodes || []

  // Sync selectedItems with selectedLevels prop
  useEffect(() => {
    setSelectedItems(selectedLevels)
  }, [selectedLevels])

  // Parse data based on feature flag
  const jobsEntities = parseTreeDataByJobs(treeLevels, expandedElements)

  const levelsEntities = parseTreeDataByLevels(treeLevels)

  // Filter out existing resource job level IDs
  const filteredJobsEntities = jobsEntities
    .map((roleEntity) => ({
      ...roleEntity,
      subItems: roleEntity.subItems?.filter(
        (subItem) => !existingResourceJobLevelIds.includes(String(subItem.subId))
      ),
    }))
    .filter((roleEntity) => roleEntity.subItems && roleEntity.subItems.length > 0)

  const filteredLevelsEntities = levelsEntities.filter(
    (levelEntity) => !existingResourceJobLevelIds.includes(levelEntity.id)
  )

  const groupMap: Record<GroupType, ParsedEntity[]> = {
    [Groups.ROLES]: filteredJobsEntities,
    [Groups.LEVELS]: filteredLevelsEntities,
  }

  const onItemExpandedChange = (id: string, expanded: boolean) => {
    if (expanded) {
      setExpandedElements([id].concat(expandedElements))
    } else {
      setExpandedElements(expandedElements.filter((el) => el !== id))
    }
  }

  const isValidGroupType = (value: string | null): value is GroupType => {
    return value === Groups.ROLES || value === Groups.LEVELS
  }

  const onGroupChange = (value: string | null) => {
    if (!isValidGroupType(value)) return

    switch (value) {
      case Groups.ROLES:
        setSelectedGroup(Groups.ROLES)
        break
      case Groups.LEVELS:
        setSelectedGroup(Groups.LEVELS)
        break
    }
  }

  // Convert selected items to EntitySelect format for each group
  const mapSelectedItemsToEntities = (
    group: GroupType,
    selectedLevelsList: SelectedLevelItem[],
    entities: ParsedEntity[]
  ): EntitySelectEntity[] => {
    if (group === Groups.ROLES) {
      const selectedLevelIds = new Set(selectedLevelsList.map((item) => item.id))

      return entities
        .map((entity) => {
          const selectedSubItems =
            entity.subItems?.filter((subItem) => selectedLevelIds.has(String(subItem.subId))) || []

          return {
            ...entity,
            subItems: selectedSubItems,
          }
        })
        .filter((entity) => entity.subItems && entity.subItems.length > 0)
    }

    if (group === Groups.LEVELS) {
      const selectedLevelIds = new Set(selectedLevelsList.map((item) => item.id))
      return entities.filter((entity) => selectedLevelIds.has(entity.id))
    }

    return []
  }

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = {
    [Groups.ROLES]: mapSelectedItemsToEntities(Groups.ROLES, selectedItems, filteredJobsEntities),
    [Groups.LEVELS]: mapSelectedItemsToEntities(
      Groups.LEVELS,
      selectedItems,
      filteredLevelsEntities
    ),
  }

  const onSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    let newSelectedItems: SelectedLevelItem[] = []

    if (selection === null) {
      newSelectedItems = []
    } else if (Array.isArray(selection)) {
      if (selectedGroup === Groups.ROLES) {
        // Handle role selection with sub-items (only save levels)
        const flatSelected = flatSelection(selection)

        newSelectedItems = flatSelected.map((subItem) => {
          const _originalLevel = treeLevels.find(
            (level) => String(level.id) === String(subItem.subId)
          )

          return {
            id: String(subItem.subId),
            name: subItem.subName,
            type: 'level',
            roleName: _originalLevel?.ancestor?.name || '',
            totalCount: _originalLevel?.referenceContractsConnection?.totalCount || 0,
            salaryRanges: [],
          }
        })
      } else if (selectedGroup === Groups.LEVELS) {
        newSelectedItems = selection.map((entity) => {
          const totalCount =
            'totalCount' in entity && typeof entity.totalCount === 'number' ? entity.totalCount : 0

          return {
            id: String(entity.id),
            name: entity.name,
            type: 'level',
            roleName: '',
            totalCount,
            salaryRanges: [], // Tree data doesn't have salaryRanges
          }
        })
      }
    } else {
      const totalCount =
        'totalCount' in selection && typeof selection.totalCount === 'number'
          ? selection.totalCount
          : 0

      newSelectedItems = [
        {
          id: String(selection.id),
          name: selection.name,
          type: 'level',
          roleName: '',
          totalCount,
          salaryRanges: [], // Tree data doesn't have salaryRanges
        },
      ]
    }

    setSelectedItems(newSelectedItems)
    setValues({ levels: newSelectedItems })
  }

  return (
    <EntitySelect
      label={i18n.t('employee_selector.select_employees')}
      hideLabel
      alwaysOpen
      clearLabel={i18n.t('common.clear')}
      entities={groupMap[selectedGroup] || []}
      groups={[
        { label: 'Roles', value: Groups.ROLES },
        { label: 'Levels', value: Groups.LEVELS },
      ]}
      hiddenAvatar
      loading={loading}
      notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
      notFoundTitle={i18n.t('employee_selector.not_found.title')}
      onItemExpandedChange={(id, expanded) => onItemExpandedChange(String(id), expanded)}
      onGroupChange={onGroupChange}
      onSelect={onSelect}
      selectedEntities={selectedGroupMap[selectedGroup]}
      searchPlaceholder={i18n.t('common.search')}
      selectAllLabel={i18n.t('common.select_all')}
      selectedGroup={selectedGroup}
      selectedLabel={i18n.t('common.selected')}
      singleSelector={false}
      selectedItemsCopy={i18n.t('employee_selector.employees_selected')}
      placeholder={i18n.t('employee_selector.select_employees')}
      width={380}
    />
  )
}

export default RolesAndLevelsEntitySelector
